import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  faArrowRight,
  faBook,
  faCheck,
  faCircleNotch,
  faEye,
  faFileContract,
  faGear,
  faQuestion,
  faSpinner,
} from '@fortawesome/pro-solid-svg-icons';

import { AsyncPipe, TitleCasePipe, UpperCasePipe } from '@angular/common';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import Keycloak from 'keycloak-js';
import { MenuItem, MessageService, ToastMessageOptions } from 'primeng/api';
import { Avatar } from 'primeng/avatar';
import { BadgeDirective } from 'primeng/badge';
import { Button } from 'primeng/button';
import { Popover } from 'primeng/popover';
import { Ripple } from 'primeng/ripple';
import { Toast } from 'primeng/toast';
import { Subject, map, takeUntil } from 'rxjs';
import { AppConfig, LayoutService } from 'src/app/layout/service/app.layout.service';
import { Wiki } from 'src/app/rgs/components/admin/components/wiki/model/wiki';
import { WikiService } from 'src/app/rgs/components/admin/components/wiki/services/wiki.service';
import { RestService } from 'src/app/services/rest.service';
import { RgsHttpErrorResponse } from 'src/app/shared/models/http-error.model';
import { PermissionsService, haveUserReadOnlyRoleForMasterData } from 'src/app/shared/services/permissions.service';
import { ArrayUtils } from 'src/app/shared/utils/array.utils';
import { ToastUtils } from 'src/app/shared/utils/toast.utils';
import { environment } from 'src/environments/environment';
import { AppBreadcrumbComponent } from '../breadcrumbs/app.breadcrumb.component';
import { AppSidebarComponent } from '../sidebar/app.sidebar.component';
import { NotificationsSidebarComponent } from './components/notifications-sidebar/notifications-sidebar.component';
import { NotesSidebarService } from './components/notifications-sidebar/services/notes-sidebar.service';
import { TaskStatus } from './components/tasks-sidebar/models/task.model';
import { TaskService } from './components/tasks-sidebar/services/task.service';
import { TasksSidebarComponent } from './components/tasks-sidebar/tasks-sidebar.component';

@Component({
  changeDetection: ChangeDetectionStrategy.Default,
  selector: 'app-topbar',
  templateUrl: './app.topbar.component.html',
  encapsulation: ViewEncapsulation.None,
  imports: [
    AppBreadcrumbComponent,
    FaIconComponent,
    Button,
    AppSidebarComponent,
    Avatar,
    Ripple,
    BadgeDirective,
    Popover,
    NotificationsSidebarComponent,
    TasksSidebarComponent,
    Toast,
    AsyncPipe,
    UpperCasePipe,
    TitleCasePipe,
    TranslateModule,
  ],
})
export class AppTopbarComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);
  private readonly messageService = inject(MessageService);
  private readonly restService = inject(RestService);
  private readonly permissionsService = inject(PermissionsService);
  readonly layoutService = inject(LayoutService);
  readonly el = inject(ElementRef);
  private readonly wikiService = inject(WikiService);

  public readonly keycloak = inject(Keycloak);
  public readonly notesSidebarService = inject(NotesSidebarService);
  public readonly taskService = inject(TaskService);
  public menuItems: MenuItem[] = [];

  public loadingWiki = this.wikiService.loadingWiki;

  public config$ = this.layoutService.config$;

  @ViewChild('menubutton')
  public menuButton!: ElementRef;

  @ViewChild(AppSidebarComponent)
  public appSidebar!: AppSidebarComponent;

  public username = '';
  public notesLength$ = this.notesSidebarService.items$.pipe(
    map(
      (items) =>
        items.filter(
          (note) =>
            !ArrayUtils.ensureArray(note?.gelezen)
              .map((read) => read.gebruiker)
              .includes(this.userName),
        ).length,
    ),
  );
  public tasksLength$ = this.taskService.items$.pipe(
    map(
      (tasks) =>
        tasks.filter((task) => task.reportStatus === TaskStatus.RUNNING || task.reportStatus === TaskStatus.QUEUED)
          .length,
    ),
  );
  public taskStatus$ = this.taskService.taskStatus$;
  public runningTaskId = this.taskService.runningTaskId;
  public user$ = this.permissionsService.user$;
  public destroy$ = new Subject<void>();

  public faSpinner = faSpinner;

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public ngOnInit(): void {
    this.taskStatus$.pipe(takeUntil(this.destroy$)).subscribe((task) => {
      let message: ToastMessageOptions | undefined;
      switch (task?.reportStatus) {
        case TaskStatus.COMPLETE:
          this.messageService.clear('task-running');
          message = {
            key: 'task-complete',
            data: task,
            severity: 'success',
            summary: this.translateService.instant('reportTasks.messages.complete.summary'),
            detail: this.translateService.instant('reportTasks.messages.complete.detail', {
              name: task?.reportName ? task?.reportName : '',
            }),
            life: 5000,
          };
          break;
        case TaskStatus.RUNNING:
          if (this.taskService.runningTaskId !== task?.uuid) {
            message = {
              closable: false,
              key: 'task-running',
              data: task,
              severity: 'info',
              summary: this.translateService.instant('reportTasks.messages.running.summary'),
              detail: this.translateService.instant('reportTasks.messages.running.detail', {
                name: task?.reportName ? task?.reportName : '',
              }),
              icon: 'fa-solid fa-circle-notch fa-spin',
              sticky: false,
            };
          }
          break;
        case TaskStatus.FAIL:
          this.messageService.clear('task-running');
          message = {
            key: 'task-fail',
            data: task,
            severity: 'error',
            summary: this.translateService.instant('reportTasks.messages.fail.summary'),
            detail: this.translateService.instant('reportTasks.messages.fail.detail', {
              name: task?.reportName ? task?.reportName : '',
            }),
            life: 5000,
          };
          break;
      }
      if (message) {
        this.messageService.add(message);
      }
    });
    this.keycloak.loadUserProfile().then((value: any) => {
      let keycloakLocale = 'en';

      if (value?.attributes?.locale && value?.attributes?.locale.length > 0) {
        keycloakLocale = value.attributes.locale[0];
      }

      localStorage.setItem('locale', keycloakLocale);
      this.translateService.use(keycloakLocale);
      this.username = this.getInitials(value.firstName + ' ' + value.lastName, 2);
    });
    this.menuItems = [
      {
        label: this.translateService.instant('topbar.menu.profile'),
        icon: 'fa fa-user',
      },
      {
        label: this.translateService.instant('topbar.menu.settings'),
        icon: 'fa fa-gear',
      },
      {
        label: this.translateService.instant('topbar.menu.notifications'),
        icon: 'fa fa-inbox',
      },
      {
        label: this.translateService.instant('topbar.menu.reports'),
        icon: 'fa fa-book',
      },
      {
        label: this.translateService.instant('topbar.menu.licentie'),
        icon: 'fa fa-id-card',
      },
    ];

    this.wikiService.currentWiki$.pipe(takeUntil(this.destroy$)).subscribe((selected: Wiki | undefined) => {
      if (selected && selected.active) {
        this.loadedWikiPage = selected;
      } else {
        this.loadedWikiPage = undefined;
      }
    });
  }
  public loadedWikiPage?: Wiki;

  public onMenuButtonClick() {
    this.layoutService.onMenuToggle();
  }

  public onProfileButtonClick() {
    this.router.navigateByUrl('/user-profile');
  }

  public onSearchClick() {
    this.layoutService.toggleSearchBar();
  }

  public onRightMenuClick() {
    this.layoutService.showRightMenu();
  }

  public logout() {
    window.sessionStorage.removeItem('environment');
    window.sessionStorage.removeItem('tenant');
    this.keycloak.logout();
  }

  public goToTask() {
    this.messageService.clear('task-complete');
    this.taskService.taskSidebarVisible$.next(true);
  }

  public showTasksSidebar() {
    this.taskService.taskSidebarVisible$.next(true);
  }

  public get logo() {
    return;
  }

  private getInitials(name: string, size: number): string {
    name = name.trim();

    if (!name) {
      return '';
    }

    const initials = name.split(' ');

    if (size && size < initials.length) {
      return this.constructInitials(initials.slice(0, size));
    } else {
      return this.constructInitials(initials);
    }
  }

  /**
   * Iterates a person's name string to get the initials of each word in uppercase.
   */
  private constructInitials(elements: string[]): string {
    if (!elements || !elements.length) {
      return '';
    }

    return elements
      .filter((element) => element && element.length > 0)
      .map((element) => element[0].toUpperCase())
      .join('');
  }

  public changeLicense(license: string) {
    if (license === this.license) {
      return;
    }

    this.restService.post('/licensecontroller', { company: license }, 'text').subscribe({
      next: () => {
        window.sessionStorage.setItem('environment', license);
        this.permissionsService.license$.next(license);
        this.taskService.unloadItems();
        const userProfile = this.permissionsService.userProfile();
        if (userProfile?.id) {
          this.permissionsService.loadUser(userProfile.id);
        }
      },
      error: (err: RgsHttpErrorResponse) => {
        ToastUtils.showError(this.messageService, null, err.error);
      },
    });
    this.router.navigate(['/']);
  }

  public isCompact(config: AppConfig) {
    return config.menuMode === 'compact';
  }

  public toggleSidebarMode() {
    this.layoutService.config$.next({
      ...this.layoutService.config$.getValue(),
      menuMode: this.isCompact(this.layoutService.config$.getValue()) ? 'static' : 'compact',
    });
  }

  public get faGear() {
    return faGear;
  }

  public get faBook() {
    return faBook;
  }

  public get faFileContract() {
    return faFileContract;
  }

  public get faCheck() {
    return faCheck;
  }

  public get faCircleNotch() {
    return faCircleNotch;
  }

  public get faArrowRight() {
    return faArrowRight;
  }

  public get faEye() {
    return faEye;
  }

  public get faQuestion() {
    return faQuestion;
  }

  public get userRoles() {
    const tokenParsed = this.keycloak.tokenParsed;
    return ArrayUtils.ensureArray(tokenParsed?.realm_access?.roles);
  }

  public get licenses(): string[] {
    return ArrayUtils.ensureArray(this.permissionsService.userProfile()?.attributes?.licentie).sort((a, b) =>
      a.localeCompare(b),
    );
  }

  public get license() {
    return this.permissionsService.license;
  }

  public get environment() {
    switch (environment.springAppUrl) {
      case 'http://localhost:8080':
        return this.translateService.instant('environments.local');
      case 'https://beta.service.rgsplus.nl':
        return this.translateService.instant('environments.beta');
      case 'https://v3.service.rgsplus.nl':
        return this.translateService.instant('environments.production');
      default:
        return this.translateService.instant('environments.notKnow');
    }
  }

  public get class() {
    switch (environment.springAppUrl) {
      case 'http://localhost:8080':
        return 'local';
      case 'https://beta.service.rgsplus.nl':
        return 'beta';
      case 'https://v3.service.rgsplus.nl':
        return 'production';
      default:
        return 'notKnow';
    }
  }

  public get showEnvironmentTile(): boolean {
    return this.class !== 'production';
  }

  public get environmentColorClasses(): string {
    switch (this.class) {
      case 'local':
        return 'bg-amber-400 text-amber-950';
      case 'beta':
        return 'bg-red-500 text-white';
      case 'production':
        return 'bg-emerald-500 text-white';
      default:
        return 'bg-gray-400 text-white';
    }
  }

  public get userName(): string {
    return this.keycloak.idTokenParsed?.['name'];
  }

  public get isUserInMaster() {
    return this.router.url.includes('/master');
  }

  public get haveUserReadOnlyRoleForMasterData() {
    return haveUserReadOnlyRoleForMasterData;
  }

  public get isKibUser() {
    const tokenParsed = this.keycloak.tokenParsed;
    const roles = ArrayUtils.ensureArray(tokenParsed?.realm_access?.roles);
    const kibRoles = ['kib_admin', 'kib_user'];

    const licenses = ArrayUtils.ensureArray(this.permissionsService.userProfile()?.attributes?.licentie);

    return licenses.length === 0 && roles.some((role) => kibRoles.includes(role));
  }

  public get logoSrc(): string {
    const tokenParsed = this.keycloak.tokenParsed;
    const roles = ArrayUtils.ensureArray(tokenParsed?.realm_access?.roles);
    const isKibCore = roles.includes('kib_core');

    return isKibCore ? 'assets/layout/images/logo_rgsnl.svg' : 'assets/layout/images/logo.svg';
  }

  public toggleMenu(menu: any, event: any) {
    menu.toggle(event);
  }

  public openWiki() {
    if (!this.loadedWikiPage) {
      return;
    }
    this.wikiService.getWikiPageAndOpenDialog(this.loadedWikiPage.pageId);
  }

  public hasRgsAdminRole() {
    const tokenParsed = this.keycloak.tokenParsed;
    const roles = ArrayUtils.ensureArray(tokenParsed?.realm_access?.roles);
    return roles.includes('rgs_admin');
  }
}
